import { ReactNode } from "react"
import { Head } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Link, usePage } from "@inertiajs/react"
import { Button } from "../../components/ui/button"
import { FileText, FolderOpen, ArrowRight, Activity } from "lucide-react"
import AdminLayout from "../../layouts/AdminLayout"

interface Activity {
  id: number
  action: string
  resource_type: string
  resource_title: string
  admin_name: string
  icon: string
  color: string
  created_at: string
}

declare module '@inertiajs/core' {
  interface PageProps {
    blog_posts_count: number
    projects_count: number
    recent_activities: Activity[]
  }
}

const AdminDashboard = () => {
  const { blog_posts_count, projects_count, recent_activities } = usePage().props
  
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    return `${Math.floor(diffInSeconds / 86400)}d ago`
  }
  
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'FileText':
        return <FileText className="h-4 w-4" />
      case 'FolderOpen':
        return <FolderOpen className="h-4 w-4" />
      default:
        return <Activity className="h-4 w-4" />
    }
  }
  
  return (
    <>
      <Head title="Dashboard" />

      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Welcome to your admin dashboard</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Blog Posts</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{blog_posts_count}</div>
              <Link href="/admin/blog_posts">
                <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto">
                  Manage posts <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{projects_count}</div>
              <Link href="/admin/projects">
                <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto">
                  Manage projects <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {recent_activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No recent activity to display
                </p>
              ) : (
                <div className="space-y-4">
                  {recent_activities.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center ${activity.color}`}>
                        {getIcon(activity.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {activity.action}
                        </p>
                        <p className="text-sm text-gray-500">
                          "{activity.resource_title}" by {activity.admin_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatTimeAgo(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

AdminDashboard.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>

export default AdminDashboard